// Shared user-facing date formatting for TFS CRM.
// Storage, API payloads and query params stay ISO-8601 — conversion happens
// only here, at the presentation layer.
// Dates:      MM/DD/YYYY
// Date+time:  MM/DD/YYYY, h:mm AM/PM (business timezone America/New_York)
const TZ = "America/New_York";

// Legacy imported records can carry Excel serial date numbers (e.g.
// "46328.291666666664"). Excel's epoch is 1899-12-30 and the fraction is the
// time of day, as a plain wall-clock value (no timezone) — so we read the
// components in UTC to avoid shifting the intended date/time.
function excelSerial(v) {
  const s = String(v).trim();
  if (!/^\d{4,6}(\.\d+)?$/.test(s)) return NaN;
  const n = parseFloat(s);
  return n >= 20000 && n <= 80000 ? n : NaN; // ~1954 to ~2119
}

function fromExcelSerial(n) {
  // 25569 = days between 1899-12-30 and 1970-01-01
  return new Date(Math.round((n - 25569) * 86400 * 1000));
}

function excelDatePart(n) {
  const d = fromExcelSerial(n);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getUTCFullYear()}`;
}

function excelTimePart(n) {
  const d = fromExcelSerial(n);
  const h = d.getUTCHours();
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 === 0 ? 12 : h % 12}:${String(d.getUTCMinutes()).padStart(2, "0")} ${ampm}`;
}

export function formatDate(v) {
  if (!v && v !== 0) return "";
  const serial = excelSerial(v);
  if (!Number.isNaN(serial)) return excelDatePart(serial);
  const s = String(v);
  // Date-only values (target close, bid due, service start, periods...):
  // parse components directly so no timezone shift can move the day.
  if (!s.includes("T")) {
    const m = s.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : s;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-US", { timeZone: TZ, month: "2-digit", day: "2-digit", year: "numeric" });
}

export function formatTime(t) {
  if (!t && t !== 0) return "";
  const s = String(t).trim();
  // Excel time-of-day fraction (0 <= n < 1), e.g. "0.2916666" -> 7:00 AM
  if (/^0?\.\d+$/.test(s)) {
    const mins = Math.round(parseFloat(s) * 1440) % 1440;
    const h = Math.floor(mins / 60);
    const ampm = h >= 12 ? "PM" : "AM";
    return `${h % 12 === 0 ? 12 : h % 12}:${String(mins % 60).padStart(2, "0")} ${ampm}`;
  }
  const serial = excelSerial(s);
  if (!Number.isNaN(serial)) return excelTimePart(serial);
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return s;
  const h = parseInt(m[1], 10);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${ampm}`;
}

export function formatDateTime(v) {
  if (!v && v !== 0) return "";
  const serial = excelSerial(v);
  if (!Number.isNaN(serial)) return `${excelDatePart(serial)}, ${excelTimePart(serial)}`;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const date = d.toLocaleDateString("en-US", { timeZone: TZ, month: "2-digit", day: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true });
  return `${date}, ${time}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "Sep 4, 2026 at 10:00 AM ET" — explicit due display for task rows.
// dateStr is a plain ISO date (wall-clock ET), timeStr optional "HH:MM".
export function formatDueET(dateStr, timeStr) {
  if (!dateStr) return "";
  const m = String(dateStr).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const datePart = m ? `${MONTHS[parseInt(m[2], 10) - 1]} ${parseInt(m[3], 10)}, ${m[1]}` : formatDate(dateStr);
  return timeStr ? `${datePart} at ${formatTime(timeStr)} ET` : `${datePart} (ET)`;
}

// "Offline — Last active 09/10/2026 at 2:14 PM ET" helper (ISO UTC input).
export function formatLastActiveET(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("en-US", { timeZone: TZ, month: "2-digit", day: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true });
  return `${date} at ${time} ET`;
}
